import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, UserPlus, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Employee } from '../../../types/employee';
import { FuncionarioEscalado } from '../../../types/contract';
import { employeeService } from '../../../services/employeeService';

interface EscalarFuncionarioSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onEscalar: (funcionario: Omit<FuncionarioEscalado, 'id'>) => void;
}

export const EscalarFuncionarioSidebar: React.FC<EscalarFuncionarioSidebarProps> = ({
  isOpen,
  onClose,
  onEscalar
}) => {
  const navigate = useNavigate();
  const [funcionariosDisponiveis, setFuncionariosDisponiveis] = useState<Employee[]>([]);
  const [loadingFuncionarios, setLoadingFuncionarios] = useState(false);
  const [novoFuncionario, setNovoFuncionario] = useState<Omit<FuncionarioEscalado, 'id'>>({
    funcionarioId: '',
    nome: '',
    funcao: '',
    valorPagamento: 0,
    observacoes: ''
  });

  // Carregar funcionários quando a sidebar for aberta
  useEffect(() => {
    if (isOpen) {
      const loadFuncionarios = async () => {
        try {
          setLoadingFuncionarios(true);
          console.log('🔍 Buscando funcionários ativos...');
          
          // Primeiro tenta buscar funcionários ativos
          let funcionarios = await employeeService.getActiveEmployees();
          console.log('✅ Funcionários ativos encontrados:', funcionarios);
          
          // Se não houver funcionários ativos, busca todos os funcionários
          if (funcionarios.length === 0) {
            console.log('⚠️ Nenhum funcionário ativo encontrado. Buscando todos os funcionários...');
            funcionarios = await employeeService.getEmployees();
            console.log('📋 Todos os funcionários:', funcionarios);
          }
          
          setFuncionariosDisponiveis(funcionarios);
          
          // Se houver apenas um funcionário, selecionar automaticamente
          if (funcionarios.length === 1) {
            const funcionario = funcionarios[0];
            setNovoFuncionario(prev => ({
              ...prev,
              funcionarioId: funcionario.id,
              nome: funcionario.nome
            }));
            console.log('🎯 Funcionário selecionado automaticamente:', funcionario.nome);
          }
        } catch (error) {
          console.error('❌ Erro ao carregar funcionários:', error);
        } finally {
          setLoadingFuncionarios(false);
        }
      };

      loadFuncionarios();
    }
  }, [isOpen]);

  const handleFuncionarioChange = (funcionarioId: string) => {
    const funcionario = funcionariosDisponiveis.find(f => f.id === funcionarioId);
    if (funcionario) {
      setNovoFuncionario(prev => ({
        ...prev,
        funcionarioId: funcionario.id,
        nome: funcionario.nome
      }));
    }
  };

  const handleSubmit = () => {
    if (novoFuncionario.funcionarioId) {
      onEscalar(novoFuncionario);
      // Reset form
      setNovoFuncionario({
        funcionarioId: '',
        nome: '',
        funcao: '',
        valorPagamento: 0,
        observacoes: ''
      });
      onClose();
    }
  };

  const handleClose = () => {
    // Reset form ao fechar
    setNovoFuncionario({
      funcionarioId: '',
      nome: '',
      funcao: '',
      valorPagamento: 0,
      observacoes: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex">
      {/* Overlay com animação de entrada */}
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-sm animate-[fadeIn_0.15s_ease-out_forwards]"
        onClick={handleClose}
      />
      
      {/* Sidebar com animação de entrada */}
      <div className="ml-auto relative bg-white shadow-2xl border-l border-slate-200 w-96 h-full overflow-hidden animate-[slideInFromRight_0.2s_ease-out_forwards]">
        {/* Header elegante */}
        <div className="relative bg-gradient-to-r from-teal-500 to-teal-600 text-white p-6">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-white/30 to-teal-400"></div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm animate-[zoomIn95_0.2s_ease-out_forwards]">
                <UserPlus className="w-5 h-5 text-white" />
              </div>
              <div className="animate-[slideInFromLeft2_0.25s_ease-out_forwards]">
                <h2 className="text-xl font-bold text-white">Escalar Funcionário</h2>
                <p className="text-sm text-teal-100 opacity-90">Configure a escalação para o evento</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-all duration-200 hover:scale-105 animate-[fadeIn_0.3s_ease-out_forwards]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Conteúdo da Sidebar */}
        <div className="flex-1 overflow-y-auto">
          {loadingFuncionarios ? (
            // Estado de loading elegante
            <div className="p-8 text-center animate-[fadeIn_0.3s_ease-out_forwards]">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-200 border-t-teal-500 mx-auto mb-4"></div>
                <div className="absolute inset-0 rounded-full bg-teal-50 animate-pulse"></div>
              </div>
              <p className="text-slate-600 font-medium">Carregando funcionários...</p>
              <p className="text-xs text-slate-400 mt-1">Aguarde um momento</p>
            </div>
          ) : funcionariosDisponiveis.length === 0 ? (
            // Estado vazio elegante
            <div className="p-8 text-center animate-[fadeIn_0.3s_ease-out_forwards]">
              <div className="relative mb-6 animate-[zoomIn95_0.35s_ease-out_forwards]">
                <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl mx-auto flex items-center justify-center">
                  <Users className="w-10 h-10 text-slate-400" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                  <span className="text-amber-600 text-xs">!</span>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2 animate-[slideInFromBottom2_0.4s_ease-out_forwards]">
                Nenhum funcionário disponível
              </h3>
              <p className="text-sm text-slate-500 mb-6 leading-relaxed animate-[slideInFromBottom2_0.45s_ease-out_forwards]">
                Para escalar funcionários para este evento, primeiro é necessário cadastrá-los no sistema.
              </p>
              <button
                onClick={() => {
                  handleClose();
                  navigate('/admin/funcionarios');
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl hover:from-teal-600 hover:to-teal-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 animate-[slideInFromBottom2_0.5s_ease-out_forwards]"
              >
                <UserPlus className="w-4 h-4" />
                Cadastrar Funcionários
              </button>
            </div>
          ) : (
            // Formulário elegante
            <div className="p-6 space-y-6 animate-[fadeIn_0.2s_ease-out_forwards]">
              {/* Seleção de Funcionário */}
              <div className="animate-[slideInFromBottom2_0.15s_ease-out_forwards]">
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  Funcionário {funcionariosDisponiveis.length === 1 ? '(Selecionado automaticamente)' : '*'}
                </label>
                {funcionariosDisponiveis.length === 1 ? (
                  <div className="w-full px-4 py-3 bg-gradient-to-r from-teal-50 to-teal-100 border-2 border-teal-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {funcionariosDisponiveis[0].nome.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-teal-800">{funcionariosDisponiveis[0].nome}</p>
                        <p className="text-xs text-teal-600">Funcionário disponível</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <select
                    value={novoFuncionario.funcionarioId}
                    onChange={(e) => handleFuncionarioChange(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 bg-white hover:border-slate-300"
                  >
                    <option value="">Selecione um funcionário</option>
                    {funcionariosDisponiveis.map(funcionario => (
                      <option key={funcionario.id} value={funcionario.id}>
                        {funcionario.nome}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Função */}
              <div className="animate-[slideInFromBottom2_0.18s_ease-out_forwards]">
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  Função no Evento
                </label>
                <input
                  type="text"
                  value={novoFuncionario.funcao}
                  onChange={(e) => setNovoFuncionario(prev => ({ ...prev, funcao: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 bg-white hover:border-slate-300"
                  placeholder="Ex: Pintura Facial, Coordenação, Animação..."
                />
              </div>

              {/* Valor */}
              <div className="animate-[slideInFromBottom2_0.21s_ease-out_forwards]">
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  Valor do Pagamento
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-500 font-medium">R$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={novoFuncionario.valorPagamento}
                    onChange={(e) => setNovoFuncionario(prev => ({ ...prev, valorPagamento: parseFloat(e.target.value) || 0 }))}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 bg-white hover:border-slate-300"
                    placeholder="0,00"
                  />
                </div>
              </div>

              {/* Observações */}
              <div className="animate-[slideInFromBottom2_0.24s_ease-out_forwards]">
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  Observações
                </label>
                <textarea
                  rows={4}
                  value={novoFuncionario.observacoes}
                  onChange={(e) => setNovoFuncionario(prev => ({ ...prev, observacoes: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-200 bg-white hover:border-slate-300 resize-none"
                  placeholder="Observações adicionais sobre a escalação..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer com botões */}
        {funcionariosDisponiveis.length > 0 && (
          <div className="border-t border-slate-200 p-6 bg-slate-50/50 animate-[slideInFromBottom2_0.27s_ease-out_forwards]">
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-3 text-slate-700 bg-white border-2 border-slate-200 hover:border-slate-300 rounded-xl transition-all duration-200 font-medium hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={!novoFuncionario.funcionarioId}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl hover:from-teal-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none disabled:shadow-none"
              >
                Escalar Funcionário
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
